import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import OBR from '@owlbear-rodeo/sdk';
import { App } from './App.tsx';
import { applyTheme } from './obr/theme.ts';
import { LocalMirrorPlayer, detectBrowserStorage } from './obr/localMirror.ts';
import { SheetWindowFrame } from './components/SheetWindowFrame.tsx';
import { notifySheetPanelMounted } from './obr/sheetPanel.ts';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('missing #root');
const root = createRoot(rootEl);
const floatingSheet = new URLSearchParams(window.location.search).get('floating') === '1';

/**
 * An OBR extension only works inside an OBR room (it needs the SDK bridge to
 * the parent frame). If someone opens the hosted URL directly, OBR.onReady
 * never fires — so we show an explanatory message instead of a blank frame.
 * This also satisfies the verified-extension guideline about functioning
 * gracefully in every configuration.
 */
function mountStandaloneNotice() {
  root.render(
    <StrictMode>
      <div className="dcc-panel">
        <h1>DCC Crawler Sheets</h1>
        <p>This is an Owlbear Rodeo extension. Open it from inside an OBR room:
          add this page’s manifest URL under Extensions.</p>
      </div>
    </StrictMode>,
  );
}

let mounted = false;
OBR.onReady(async () => {
  mounted = true;
  // Apply the current OBR theme, then follow any light/dark change the GM makes.
  if (floatingSheet) await notifySheetPanelMounted();
  applyTheme(await OBR.theme.getTheme());
  OBR.theme.onChange(applyTheme);
  const role = await OBR.player.getRole();
  // OBR drops player metadata on reload; restore this room's saved copy from
  // browser storage BEFORE anything reads the store.
  const player = new LocalMirrorPlayer(OBR.player, detectBrowserStorage(), OBR.room.id);
  await player.restore();
  root.render(
    <StrictMode>
      <SheetWindowFrame floating={floatingSheet}>
        <App role={role} player={player} />
      </SheetWindowFrame>
    </StrictMode>,
  );
});

// If OBR never becomes ready shortly, we're almost certainly outside a room.
setTimeout(() => { if (!mounted) mountStandaloneNotice(); }, 1500);
